import React, { useState } from "react";
import { Box, Typography, Stack } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { formatCurrencyNew } from "utils/helper";

const AddonSelector = ({
  addonGroups,
  selectedAddons,
  onAddonChange,
  currencySymbol,
  errors,
}) => {
  const [activeGroupId, setActiveGroupId] = useState(null);

  if (!addonGroups || addonGroups.length === 0) return null;

  const sorted = [...addonGroups].sort(
    (a, b) => (a.display_order || 0) - (b.display_order || 0),
  );

  const activeGroup =
    activeGroupId !== null
      ? sorted.find((g) => g.group_id === activeGroupId)
      : null;

  const handleGroupClick = (groupId) =>
    setActiveGroupId((prev) => (prev === groupId ? null : groupId));

  const handleOptionClick = (group, optionId) => {
    const current = selectedAddons[group.group_id] || [];
    const isSelected = current.includes(optionId);

    if (group.max_select === 1) {
      if (isSelected && group.min_select === 0) {
        onAddonChange(group.group_id, []);
      } else {
        onAddonChange(group.group_id, [optionId]);
      }
    } else {
      if (isSelected) {
        onAddonChange(group.group_id, current.filter((id) => id !== optionId));
      } else {
        if (current.length >= group.max_select) return;
        onAddonChange(group.group_id, [...current, optionId]);
      }
    }
  };

  const hint = (group) => {
    if (group.min_select > 0 && group.max_select === 1) return "Choose 1";
    if (group.min_select > 0 && group.max_select > 1)
      return `Choose ${group.min_select}–${group.max_select}`;
    if (group.max_select === 1) return "Choose up to 1";
    return `Choose up to ${group.max_select}`;
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        sx={{
          fontSize: "0.95rem",
          fontWeight: 400,
          lineHeight: 1,
          mb: 1.5,
          color: "#040404",
        }}
      >
        Select a choice of add-on
      </Typography>
      {/* ── Horizontal group cards ── */}
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
        {sorted.map((group) => {
          const selected = selectedAddons[group.group_id] || [];
          const isActive = activeGroupId === group.group_id;
          const hasError = Boolean(errors?.[group.group_id]);
          const count = selected.length;

          return (
            <Box
              key={group.group_id}
              onClick={() => handleGroupClick(group.group_id)}
              sx={{
                position: "relative",
                px: 1.1,
                py: 0.5,
                cursor: "pointer",
                border: hasError
                  ? "1px solid #d32f2f"
                  : isActive
                    ? "1px solid #111"
                    : "1px solid #ddd",
                borderRadius: "5px",
                backgroundColor: isActive ? "#111" : "#fff",
                transition: "all 0.18s ease",
                userSelect: "none",
                "&:hover": {
                  borderColor: isActive ? "#111" : "#999",
                  backgroundColor: isActive ? "#111" : "#f7f7f7",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  color: isActive ? "#fff" : "#222",
                  lineHeight: 1.2,
                }}
              >
                {group.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: isActive ? "rgba(255,255,255,0.65)" : "#999",
                  mt: 0.1,
                  lineHeight: 1,
                }}
              >
                {hint(group)}
              </Typography>

              {/* Selection count badge */}
              {count > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    width: 15,
                    height: 15,
                    borderRadius: "50%",
                    backgroundColor: isActive ? "#fff" : "#111",
                    color: isActive ? "#111" : "#fff",
                    fontSize: "0.58rem",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 0 0 2px #fff",
                  }}
                >
                  {count}
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>

      {/* ── Options panel for active group ── */}
      {activeGroup && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            border: "1px solid #111",
            borderRadius: "5px",
            backgroundColor: "#fafafa",
          }}
        >
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {[...activeGroup.options]
              .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
              .map((option) => {
                const selected = selectedAddons[activeGroup.group_id] || [];
                const isSelected = selected.includes(option.option_id);
                const atMax =
                  !isSelected &&
                  activeGroup.max_select > 1 &&
                  selected.length >= activeGroup.max_select;

                return (
                  <Box
                    key={option.option_id}
                    onClick={() =>
                      !atMax && handleOptionClick(activeGroup, option.option_id)
                    }
                    sx={{
                      px: 1,
                      py: 0.6,
                      cursor: atMax ? "not-allowed" : "pointer",
                      border: isSelected ? "1px solid #111" : "1px solid #ddd",
                      borderRadius: "5px",
                      backgroundColor: isSelected ? "#111" : "#fff",
                      transition: "all 0.15s ease",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "1px",
                      minWidth: 52,
                      opacity: atMax ? 0.4 : 1,
                      "&:hover": {
                        borderColor: atMax ? "#ddd" : "#111",
                        backgroundColor: atMax
                          ? "#fff"
                          : isSelected
                            ? "#222"
                            : "#f0f0f0",
                      },
                    }}
                  >
                    {isSelected && (
                      <CheckIcon
                        sx={{ fontSize: 11, color: "#fff", mb: "-1px" }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontSize: "0.68rem",
                        fontWeight: 500,
                        color: isSelected ? "#fff" : "#222",
                        textAlign: "center",
                        lineHeight: 1.2,
                      }}
                    >
                      {option.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.6rem",
                        color: isSelected ? "rgba(255,255,255,0.75)" : "#777",
                      }}
                    >
                      +{formatCurrencyNew(option.price, currencySymbol)}
                    </Typography>
                  </Box>
                );
              })}
          </Stack>

          {/* Error inside options panel */}
          {errors?.[activeGroup.group_id] && (
            <Typography
              variant="caption"
              sx={{ color: "#d32f2f", display: "block", mt: 0.75, fontSize: "0.68rem" }}
            >
              {errors[activeGroup.group_id]}
            </Typography>
          )}
        </Box>
      )}

      {/* Errors for groups that are not currently open */}
      {sorted.map((group) =>
        errors?.[group.group_id] && group.group_id !== activeGroupId ? (
          <Typography
            key={group.group_id}
            variant="caption"
            sx={{ color: "#d32f2f", display: "block", mt: 0.5, fontSize: "0.68rem" }}
          >
            {group.name}: {errors[group.group_id]}
          </Typography>
        ) : null,
      )}
    </Box>
  );
};

export default AddonSelector;
